import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Headphones } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SeoHead } from "@/components/SeoHead";
import { staggerContainer, fadeUp } from "@/lib/motion";

export default function Iletisim() {
  const { t } = useTranslation();

  const emails = [
    { key: "general", icon: Mail, label: t("contact.emailGeneralLabel"), email: t("contact.emailGeneral") },
    { key: "sales", icon: Headphones, label: t("contact.emailSalesLabel"), email: t("contact.emailSales") },
  ];

  const phone = t("contact.phone");
  const address = t("contact.address");
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

  return (
    <div data-testid="iletisim-page" className="py-20 md:py-28">
      <SeoHead pageKey="contact" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-sm tracking-widest uppercase text-[#2b8a7e] mb-5">
            {t("contact.overline")}
          </p>
          <h1 className="font-display font-bold text-[#1a3636] text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-none max-w-4xl">
            {t("contact.title")}
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#1a3636]/65 leading-relaxed max-w-2xl">
            {t("contact.sub")}
          </p>
        </Reveal>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6"
        >
          {/* Address card */}
          <motion.a
            variants={fadeUp}
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-address-card"
            className="group lg:col-span-7 bg-white rounded-2xl p-8 border border-[#1a3636]/5 shadow-[0_8px_30px_rgb(26,54,54,0.06)] hover:shadow-[0_20px_40px_rgb(43,138,126,0.12)] transition-all duration-500"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#2b8a7e]/10 flex items-center justify-center text-[#2b8a7e] group-hover:bg-[#2b8a7e] group-hover:text-white transition-colors duration-500">
                <MapPin size={22} strokeWidth={1.5} />
              </div>
              <span className="text-xs uppercase tracking-widest text-[#2b8a7e]">
                {t("contact.addressLabel")}
              </span>
            </div>
            <p className="font-display text-2xl md:text-3xl font-semibold text-[#1a3636] tracking-tight leading-snug">
              {address}
            </p>
            <p className="mt-4 text-sm text-[#1a3636]/50 group-hover:text-[#2b8a7e] transition-colors">
              Google Maps üzerinde aç →
            </p>
          </motion.a>

          {/* Phone card */}
          <motion.a
            variants={fadeUp}
            href={`tel:${phone.replace(/\s+/g, "")}`}
            data-testid="contact-phone-card"
            className="group lg:col-span-5 bg-[#1a3636] text-[#f8faf9] rounded-2xl p-8 border border-white/5 shadow-[0_8px_30px_rgb(26,54,54,0.12)] hover:bg-[#2b8a7e] transition-colors duration-500"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#f8faf9]/10 flex items-center justify-center text-[#f8faf9]">
                <Phone size={22} strokeWidth={1.5} />
              </div>
              <span className="text-xs uppercase tracking-widest text-[#2b8a7e] group-hover:text-white transition-colors">
                {t("contact.phoneLabel")}
              </span>
            </div>
            <p className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              {phone}
            </p>
            <p className="mt-4 text-sm text-[#f8faf9]/50">Aramak için dokunun →</p>
          </motion.a>

          {/* Emails */}
          <motion.div variants={fadeUp} className="lg:col-span-12">
            <p className="text-xs uppercase tracking-widest text-[#2b8a7e] mb-4">
              {t("contact.emailsLabel")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {emails.map((em) => {
                const Icon = em.icon;
                return (
                  <a
                    key={em.key}
                    href={`mailto:${em.email}`}
                    data-testid={`contact-email-${em.key}`}
                    className="group bg-white rounded-2xl p-6 border border-[#1a3636]/5 shadow-[0_8px_30px_rgb(26,54,54,0.06)] hover:shadow-[0_20px_40px_rgb(43,138,126,0.12)] transition-all duration-500"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#2b8a7e]/10 flex items-center justify-center text-[#2b8a7e] mb-4 group-hover:bg-[#2b8a7e] group-hover:text-white transition-colors duration-500">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <p className="text-[11px] uppercase tracking-widest text-[#1a3636]/50 mb-1">
                      {em.label}
                    </p>
                    <p className="font-display text-lg font-semibold text-[#1a3636] tracking-tight break-all">
                      {em.email}
                    </p>
                  </a>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
